import React, { useState, useMemo } from "react";
import { useIntl } from "react-intl";
import { Modal, Button, Form } from "react-bootstrap";
import axios from "axios";
import { getAuth } from "../../../app/modules/auth";
import { showErrorMessage } from "../../helpers/MessagesHelper";
import { toastfy } from "../toast/Toast";
import { useStyleContext } from "../../helpers/StyleContext";

import { TablesWidget11 } from "../widgets/tables/TablesWidget15"; 

interface Props {
  show: boolean;
  onHide: () => void;
  contract: any;
  _user_id: number; // 🟢 NOVO: ID do usuário
}

const UserInvoicesModal: React.FC<Props> = ({ show, onHide, contract, _user_id }) => {
  const intl = useIntl();
  const { styleState } = useStyleContext();

  const [dashboardUpdate, setDashboardUpdate] = useState(false);

  const [showPixModal, setShowPixModal] = useState(false);
  const [pixData, setPixData] = useState<{
    encodedImage?: string;
    payload?: string;
  } | null>(null);
  const [loadingPix, setLoadingPix] = useState(false);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [currentCharge, setCurrentCharge] = useState<any>(null);
  const [loadingUpload, setLoadingUpload] = useState(false);

  const [showProofModal, setShowProofModal] = useState(false);
  const [selectedProofUrl, setSelectedProofUrl] = useState('');

  const API_URL = process.env.REACT_APP_API_URL;

  // 🟢 NOVO CAMINHO: Apontando para users/{user_id}/contracts/{contract_id}
  const modulePath = useMemo(() => {
    return contract?._id 
      ? `users/${_user_id}/contracts-invoices/${contract._id}` 
      : `users/${_user_id}/contracts`;
  }, [contract, _user_id]);

  const generatePix = async (item: any) => {
    const status = item?.status?.description?.toLowerCase();
    if (status !== "pending" && status !== "pendente") {
      toastfy("warning", "O pagamento via PIX só está disponível para cobranças pendentes.");
      return;
    }

    setLoadingPix(true);
    setShowPixModal(true);
    setPixData(null);

    try {
      const auth = getAuth();
      const token = auth?.api_token;
      const contractId = item?.contract?._id || contract?._id;

      // 🟢 ROTA DO PIX ATUALIZADA
      const response = await axios.get(
        `${API_URL}/users/${_user_id}/contracts/${contractId}/charges/${item._id}/pix`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPixData(response.data);
    } catch (error: any) {
      setShowPixModal(false);
      showErrorMessage(error?.response?.data?.error?.statusCode || 500, "Erro ao gerar PIX");
    } finally {
      setLoadingPix(false);
    }
  };

  const handleUploadProof = async () => {
    if (!selectedFile || !currentCharge) return;

    const allowedTypes = ["image/png", "image/jpeg", "application/pdf"];
    if (!allowedTypes.includes(selectedFile.type)) {
      showErrorMessage(500, "Formato de arquivo inválido. Envie apenas PNG, JPEG ou PDF.");
      return;
    }
    
    setLoadingUpload(true);
    try {
      const auth = getAuth();
      const token = auth?.api_token;
      const formData = new FormData();
      formData.append("file", selectedFile);

      const contractId = currentCharge?.contract?._id || contract?._id;

      // 🟢 ROTA DE UPLOAD ATUALIZADA
      await axios.post(
        `${API_URL}/users/${_user_id}/contracts/${contractId}/charges/${currentCharge._id}/proof`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      toastfy("success", "Comprovante enviado com sucesso!");
      setShowUploadModal(false);
      setSelectedFile(null);
      setDashboardUpdate(!dashboardUpdate); 
    } catch (error) {
      showErrorMessage(500, "Erro ao enviar comprovante");
    } finally {
      setLoadingUpload(false);
    }
  };

  const copyPixToClipboard = () => {
    if (pixData?.payload) {
      navigator.clipboard.writeText(pixData.payload);
      alert("Código PIX copiado!");
    }
  };

  const columns = [
    { description: "ID", className: "w-50px", reference: "_id" },
    { description: "Vencimento", className: "min-w-100px", reference: "expiration_date", type: "date" },
    { description: "Pagamento", className: "min-w-100px", reference: "payment_date", type: "date" },
    { description: "Valor", className: "min-w-100px", reference: "value", type: "decimal" },
    { description: "Status", className: "min-w-100px", reference: "status.description", type: "status-text" },
    { description: "Ações", className: "text-end min-w-80px", reference: "actions" },
  ];

  const actions = [
    { title: "Pagar via PIX", icon: "/media/icons/duotune/finance/fin010.svg", url: "", type: "pix", onClick: generatePix },
    {
      title: "Enviar Comprovante",
      icon: "/media/icons/duotune/files/fil003.svg",
      url: "",
      type: "upload",
      onClick: (item: any) => {
        setCurrentCharge(item);
        setShowUploadModal(true);
      },
    },
    { title: "Visualizar Comprovante", icon: "/media/icons/duotune/general/gen001.svg", url: "", type: "custom" }
  ];

  const customActionHandler = (item: any, action: any) => {
    if (action.title === "Visualizar Comprovante") {
      if (!item.comprovante_url) return null;
      return (
        <div className="d-inline-block me-1 mb-1">
          <button
            type="button"
            className="btn btn-icon btn-sm w-30px h-30px btn-bg-light btn-active-color-primary"
            style={{ border: "1px solid #8c8e96" }}
            title="Visualizar Comprovante"
            onClick={(e) => {
              e.preventDefault();
              setSelectedProofUrl(item.comprovante_url);
              setShowProofModal(true);
            }}
          >
             <i className="fa fa-eye fs-6"></i>
          </button>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered style={{ zIndex: 1060 }}>
        <Modal.Header closeButton>
          <Modal.Title>
            Faturas do Contrato - {contract?.description || "Selecionado"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: "60vh", backgroundColor: "#f5f8fa" }}>
          {contract ? (
            <TablesWidget11
              className="mb-5 mb-xl-8"
              nameModule="Faturas"
              descriptionModule={`Listagem de cobranças do contrato`}
              module={modulePath}
              moduleData="charges"
              columns={columns}
              actions={actions}
              customActionHandler={customActionHandler}
              setDashboardUpdate={setDashboardUpdate}
              customFilters={{ situationFilter: true, pageFilter: true }}
            />
          ) : (
            <p className="text-center text-muted">Carregando...</p>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={showPixModal} onHide={() => setShowPixModal(false)} centered style={{ zIndex: 1070 }}>
        <Modal.Header closeButton>
          <Modal.Title>Pagamento via PIX</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {loadingPix ? (
            <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Carregando...</span></div>
          ) : pixData ? (
            <div>
              <p className="text-muted mb-4">Escaneie o QR Code abaixo no aplicativo do seu banco:</p>
              <img
                src={`data:image/png;base64,${pixData.encodedImage}`}
                alt="QR Code PIX"
                style={{ width: "250px", height: "250px", marginBottom: "20px" }}
              />
              <p className="text-muted mt-4">Ou copie o código PIX Copia e Cola:</p>
              <div className="input-group mt-2">
                <input type="text" className="form-control form-control-solid text-center" value={pixData.payload} readOnly />
                <button className="btn btn-primary" type="button" onClick={copyPixToClipboard}>Copiar</button>
              </div>
            </div>
          ) : (
            <p className="text-danger">Não foi possível carregar os dados do PIX.</p>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)} centered style={{ zIndex: 1070 }}>
        <Modal.Header closeButton>
          <Modal.Title>Enviar Comprovante</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Selecione o arquivo</Form.Label>
            <Form.Control
              type="file"
              className="form-control form-control-solid w-100"
              accept="image/png, image/jpeg, application/pdf"
              style={{ opacity: 1, visibility: "visible", height: "auto", display: "block" }}
              onChange={(e: any) => setSelectedFile(e.target.files[0])}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUploadModal(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleUploadProof} disabled={loadingUpload}>
            {loadingUpload ? "Enviando..." : "Enviar"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showProofModal} onHide={() => setShowProofModal(false)} centered size="lg" style={{ zIndex: 1070 }}>
        <Modal.Header closeButton>
          <Modal.Title>Visualizar Comprovante</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
            <img
              src={`${API_URL}${selectedProofUrl}`}
              alt="Comprovante"
              style={{ maxWidth: "100%", height: "auto" }}
            />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default UserInvoicesModal;